import {environment} from '../../../environments/environment';
import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {ChatbotAvailableProfileRepresentation} from '../state/chatbot-available-profile-representation';
import {map, timeout} from 'rxjs/operators';
import {ChatbotAvailableProfile} from '../state/chatbot-available-profile';
import {PaginatedTeamsRepresentation} from '../state/paginated-teams-representation';
import {ChatbotAvailableTeam} from '../state/chatbot-available-team';
import {ChatbotUserRepresentation} from '../state/chatbot-profile';
import {TestService} from '../../test/state/test.service';
import {FriendshipQuery} from '../../friend/state/friendship.query';
import {TestVariation} from '../../test/state/test-variation';
import {FriendshipFunctions} from '../../friend/state/friendship-functions';
import {CurrentUser} from '../../user/current-user.service';

const API_URL = environment.API_URL;
const TIMEOUT_DURATION = 180000;

@Injectable({
  providedIn: 'root'
})

export class ChatbotProfileService {

  constructor(private http: HttpClient,
              private testService: TestService,
              private friendshipQuery: FriendshipQuery,
              private currentUserService: CurrentUser) {
  }

  public loadProfilesPaginated(pageNumber: number = 1, pageSize: number = 6){
    const params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString());
    return this.http.get<{ users: ChatbotAvailableProfileRepresentation[]; totalElements: number }>(
      API_URL + '/users/all-connected',
      {
        headers: new HttpHeaders({
          Accept: 'application/json; version=1'
        }),
        observe: 'response',
        params
      }
    ).pipe(
      timeout(TIMEOUT_DURATION),
      map(response => ({
        availableProfiles: response.body.users.map(chatbotAvailableProfileRepresentation =>
          ChatbotAvailableProfile.buildFrom(chatbotAvailableProfileRepresentation)
        ),
        numberOfAvailableProfiles: response.body.totalElements
      })
      )
    );
  }

  public loadTeamsPaginated(pageNumber: number = 1, pageSize: number = 6){
    const params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString());
    return this.http.get<PaginatedTeamsRepresentation>(
      API_URL + '/team/paginated',
      {
        headers: new HttpHeaders({
          Accept: 'application/json; version=1'
        }),
        observe: 'response',
        params
      }
    ).pipe(
      timeout(TIMEOUT_DURATION),
      map(response => ({
        availableTeams: response.body.teams.map(teamBasicInfoRepresentation =>
          ChatbotAvailableTeam.buildFrom(teamBasicInfoRepresentation)
        ),
        numberOfAvailableTeams: response.body.totalElements
      }))
    );
  }

  public getUsersFromKeycloakIds(keycloakIds: string[]){
    return this.http.post<ChatbotUserRepresentation[]>(
      API_URL + '/user-from-keycloak-id/list',
      keycloakIds
    );
  }

  public getUsersWithTests(keycloakIds: string[]){
    return this.getUsersFromKeycloakIds(keycloakIds).pipe(
      map((users: ChatbotUserRepresentation[]) =>
        users.map(user => this.addVariationToUser(user))
      )
    );
  }

  private addVariationToUser(user: ChatbotUserRepresentation): ChatbotUserRepresentation {
    try {
      const friendship = this.friendshipQuery.getFriendshipWith(user.keycloakId);

      if (friendship && friendship.testShares && friendship.testShares.length > 0) {
        const variation = FriendshipFunctions
          .getFriendTestShare(friendship, this.currentUserService.keycloakId)
          .variation;
        return {
          ...user,
          variation
        };
      }
      else {
        return {
          ...user,
          variation: TestVariation.ID37_16
        };
      }
    }
    catch (error) {
      return {
        ...user,
        variation: TestVariation.ID37_16
      };
    }
  }

}
